import { Col, DatePicker, Form, Input, InputNumber, Row, Select, Typography } from 'antd';
import type { FormInstance } from 'antd/es/form';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { PROJECT_CATEGORY_OPTIONS } from '@/constants/project';
import { ProjectDepartmentSelector } from '@/pages/admin/projects/ProjectDepartmentSelector';
import { toManagerSelectOptions } from '@/pages/admin/projects/project-manager-options';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import type {
  CreateProjectPayload,
  ProjectCategory,
  UpdateProjectPayload,
} from '@/types/project';

export interface ProjectFormValues {
  name: string;
  projectCategories: ProjectCategory[];
  constructionUnit: string;
  totalInvestment: string;
  annualPlanYear: number;
  annualPlan: string;
  remarks?: string;
  summary?: string;
  dateRange?: [Dayjs, Dayjs];
  departmentIds: string[];
  projectManagerId: string;
}

interface ProjectFormFieldsProps {
  form: FormInstance<ProjectFormValues>;
  projectId?: string;
  disabled?: boolean;
}

export function ProjectFormFields({ form, projectId, disabled = false }: ProjectFormFieldsProps) {
  const departmentIds = Form.useWatch('departmentIds', form) ?? [];
  const annualPlanYear = Form.useWatch('annualPlanYear', form) ?? new Date().getFullYear();
  const [managerLoading, setManagerLoading] = useState(false);
  const [managerOptions, setManagerOptions] = useState<{ value: string; label: string }[]>([]);
  const prevDepartmentIdsRef = useRef<string[] | null>(null);

  useEffect(() => {
    if (departmentIds.length === 0) {
      setManagerOptions([]);
      // 仅当用户主动清空部门时清除负责人，避免编辑回显前误清空
      if (prevDepartmentIdsRef.current && prevDepartmentIdsRef.current.length > 0) {
        form.setFieldValue('projectManagerId', undefined);
      }
      prevDepartmentIdsRef.current = departmentIds;
      return;
    }

    prevDepartmentIdsRef.current = departmentIds;
    let cancelled = false;
    setManagerLoading(true);

    const load = async () => {
      try {
        const result = projectId
          ? await projectsApi.fetchProjectManagerCandidates(projectId, departmentIds)
          : await projectsApi.fetchManagerCandidates(departmentIds);
        if (cancelled) {
          return;
        }
        const options = toManagerSelectOptions(result.items);
        setManagerOptions(options);
        const currentManagerId = form.getFieldValue('projectManagerId') as string | undefined;
        // 创建时：负责人不在候选范围则清空；编辑时保留原值，由后端校验
        if (
          !projectId
          && currentManagerId
          && !options.some((option) => option.value === currentManagerId)
        ) {
          form.setFieldValue('projectManagerId', undefined);
        }
      } catch (error) {
        if (!cancelled) {
          setManagerOptions([]);
          if (error instanceof ApiError) {
            form.setFields([{ name: 'projectManagerId', errors: [error.message] }]);
          }
        }
      } finally {
        if (!cancelled) {
          setManagerLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [departmentIds, form, projectId]);

  return (
    <>
      <Typography.Title level={5}>督查台账信息</Typography.Title>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="projectCategories"
            label="项目分类"
            rules={[{ required: true, message: '请至少选择一个项目分类', type: 'array' }]}
          >
            <Select
              mode="multiple"
              options={PROJECT_CATEGORY_OPTIONS}
              placeholder="可多选"
              maxTagCount="responsive"
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="name"
            label="项目名称"
            rules={[{ required: true, message: '请输入项目名称' }, { max: 128 }]}
          >
            <Input placeholder="请输入项目名称" disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="constructionUnit"
            label="建设单位"
            rules={[{ required: true, message: '请输入建设单位' }, { max: 255 }]}
          >
            <Input placeholder="请输入建设单位全称" disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="totalInvestment"
            label="总投资"
            rules={[
              { required: true, message: '请输入总投资' },
              {
                pattern: /^(?:0|[1-9]\d{0,17})(?:\.\d{1,2})?$/,
                message: '请输入正确金额，最多保留两位小数',
              },
            ]}
          >
            <Input
              inputMode="decimal"
              placeholder="请输入总投资金额"
              addonAfter="元"
              disabled={disabled}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            name="annualPlanYear"
            label="计划年度"
            rules={[{ required: true, message: '请选择计划年度' }]}
          >
            <InputNumber
              min={2000}
              max={2100}
              precision={0}
              addonAfter="年"
              style={{ width: '100%' }}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={16}>
          <Form.Item name="dateRange" label="项目周期">
            <DatePicker.RangePicker style={{ width: '100%' }} disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="annualPlan"
        label={`${annualPlanYear}年推进计划`}
        rules={[{ required: true, message: '请输入年度推进计划' }, { max: 10000 }]}
      >
        <Input.TextArea
          rows={5}
          placeholder="请输入年度建设目标、关键节点和计划安排"
          showCount
          maxLength={10000}
          disabled={disabled}
        />
      </Form.Item>

      <Form.Item name="summary" label="项目概况" rules={[{ max: 1000 }]}>
        <Input.TextArea
          rows={3}
          placeholder="选填，最多 1000 字"
          showCount
          maxLength={1000}
          disabled={disabled}
        />
      </Form.Item>

      <Form.Item name="remarks" label="备注" rules={[{ max: 2000 }]}>
        <Input.TextArea
          rows={3}
          placeholder="选填"
          showCount
          maxLength={2000}
          disabled={disabled}
        />
      </Form.Item>

      <Typography.Title level={5}>负责人和成员范围</Typography.Title>

      <Form.Item
        name="departmentIds"
        label="参与部门（成员范围）"
        rules={[
          {
            validator: async (_, ids: string[] | undefined) => {
              if (!ids || ids.length === 0) {
                throw new Error('请至少添加一个项目部门');
              }
            },
          },
        ]}
        extra="仅用于计算项目成员和查看权限，不作为督查台账中的责任单位"
      >
        <ProjectDepartmentSelector disabled={disabled} />
      </Form.Item>

      <Form.Item
        name="projectManagerId"
        label="项目负责人（周报填报人）"
        rules={[{ required: true, message: '请选择项目负责人' }]}
        extra="该负责人负责提交项目周报；候选人来自所选参与部门及其下级部门"
      >
        <Select
          showSearch
          allowClear
          optionFilterProp="label"
          options={managerOptions}
          loading={managerLoading}
          disabled={disabled || departmentIds.length === 0}
          placeholder={departmentIds.length === 0 ? '请先添加项目部门' : '请选择项目负责人'}
        />
      </Form.Item>
    </>
  );
}

export function projectFormValuesFromDetail(
  project: {
    name: string;
    projectCategories: ProjectCategory[];
    constructionUnit: string | null;
    totalInvestment: string | null;
    annualPlanYear: number | null;
    annualPlan: string | null;
    remarks: string | null;
    summary: string | null;
    startDate: string | null;
    endDate: string | null;
    projectManagerId: string;
    departments?: { id: string }[];
  },
): ProjectFormValues {
  const dateRange = parseProjectDateRange(project.startDate, project.endDate);

  return {
    name: project.name,
    projectCategories: project.projectCategories ?? [],
    constructionUnit: project.constructionUnit ?? '',
    totalInvestment: project.totalInvestment ?? '',
    annualPlanYear: project.annualPlanYear ?? new Date().getFullYear(),
    annualPlan: project.annualPlan ?? '',
    remarks: project.remarks ?? undefined,
    summary: project.summary ?? undefined,
    dateRange,
    departmentIds: project.departments?.map((department) => department.id) ?? [],
    projectManagerId: project.projectManagerId,
  };
}

export function buildProjectPayload(values: ProjectFormValues): CreateProjectPayload;
export function buildProjectPayload(
  values: ProjectFormValues,
  mode: 'update',
): UpdateProjectPayload;
export function buildProjectPayload(
  values: ProjectFormValues,
  mode: 'create' | 'update' = 'create',
): CreateProjectPayload | UpdateProjectPayload {
  const start = values.dateRange?.[0];
  const end = values.dateRange?.[1];
  const hasValidRange = Boolean(start?.isValid() && end?.isValid());

  const base = {
    name: values.name.trim(),
    projectCategories: values.projectCategories,
    constructionUnit: values.constructionUnit.trim(),
    totalInvestment: values.totalInvestment.trim(),
    annualPlanYear: values.annualPlanYear,
    annualPlan: values.annualPlan.trim(),
    remarks:
      mode === 'update' ? values.remarks?.trim() ?? '' : values.remarks?.trim() || undefined,
    summary:
      mode === 'update' ? values.summary?.trim() ?? '' : values.summary?.trim() || undefined,
    departmentIds: values.departmentIds,
    projectManagerId: values.projectManagerId,
  };

  if (hasValidRange) {
    return {
      ...base,
      startDate: start!.format('YYYY-MM-DD'),
      endDate: end!.format('YYYY-MM-DD'),
    };
  }

  if (mode === 'update') {
    return {
      ...base,
      startDate: null,
      endDate: null,
    };
  }

  return base;
}

function parseProjectDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): [Dayjs, Dayjs] | undefined {
  if (typeof startDate !== 'string' || typeof endDate !== 'string') {
    return undefined;
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) {
    return undefined;
  }

  return [start, end];
}
